<script setup lang="ts">
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import { useAuthStore } from '@/modules/authStore/stores/authStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { sanitize } from 'isomorphic-dompurify'

const userStore = useUserStore()
const authStore = useAuthStore()
const feedbackStore = useFeedbackStore()

const consentText = ref('')
const isLoading = ref(true)
const consentRef = ref<HTMLElement | null>(null)
const scrolled = ref(false)
const loadingError = ref(false)

const checkScroll = () => {
  const element = consentRef.value
  if (!element) return
  if (element.scrollHeight - element.scrollTop - element.clientHeight <= 10) {
    scrolled.value = true
  }
}

onMounted(async () => {
  try {
    isLoading.value = true
    const text = await userStore.getConsentText()
    consentText.value = sanitize(text, { ADD_ATTR: ['target'] })
    setTimeout(() => {
      checkScroll()
    }, 50)
  } catch (error) {
    consentText.value = 'Ошибка при получении текста согласия'
    console.error('[ConsentPdDialog] Ошибка при получении текста согласия:', error)
    loadingError.value = true
  } finally {
    isLoading.value = false
  }
})

function handleRead() {
  const element = consentRef.value
  if (!element) return

  element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' })
  scrolled.value = true
}

async function handleAccept() {
  await userStore.agreeToConsent()
}

async function handleDecline() {
  await authStore.logout()
}
</script>

<template>
  <VDialog
    model-value
    max-width="700"
    persistent
    scrollable
  >
    <VCard class="pa-6 d-flex flex-column gap-6">
      <!-- Шапка -->
      <div class="d-flex flex-column gap-2">
        <div class="d-flex align-center w-100">
          <span class="text-h4 font-weight-bold">
            Согласие на обработку персональных данных
          </span>
        </div>
        <CustomDivider />
        <p class="text-body-1 text-medium-emphasis">
          Для продолжения использования цифровой экосистемы DAO-MED.ru необходимо принять согласие на обработку персональных данных.
        </p>
      </div>

      <!-- Тело (прокручиваемое) -->
      <div
        ref="consentRef"
        class="pr-2"
        style="max-height: 45vh; min-height: 150px; overflow-y: scroll; scrollbar-gutter: stable;"
        @scroll="checkScroll"
      >
        <div v-if="isLoading && !feedbackStore.isGlobalLoading" class="d-flex align-center justify-center py-10 w-100">
          <VProgressCircular
            indeterminate
            color="primary"
            size="38"
            width="3"
          />
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-else class="consent-html-text" v-html="consentText" />
      </div>

      <!-- Футер -->
      <div class="d-flex align-center justify-end gap-3">
        <VBtn
          variant="outlined"
          color="secondary"
          :disabled="isLoading"
          @click="handleDecline"
        >
          Не согласен
        </VBtn>
        <VBtn
          color="primary"
          :disabled="isLoading || loadingError"
          @click="scrolled ? handleAccept() : handleRead()"
        >
          {{ scrolled ? 'Согласен' : 'Прочитать' }}
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.consent-html-text {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));

  :deep(h1),
  :deep(h2),
  :deep(h3) {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: rgb(var(--v-theme-primary));
  }

  :deep(p) {
    margin-bottom: 0.75rem;
  }

  :deep(ul),
  :deep(ol) {
    margin-bottom: 0.75rem;
    padding-left: 2.25rem;
  }

  :deep(li) {
    margin-bottom: 0.25rem;
  }
}
</style>
